import React, { useCallback, useMemo, useState } from "react";
import { useField } from "formik";
import { TransactionListItem } from "@common/schemas/transaction.schema";
import { RuleGroupType, TransactionRule } from "@common/types/transactionRule";
import { calculateSplitSiblings } from "@helpers/transactionSplitUtils";
import { SplitConfigSchema } from "@common/schemas/rule.schema";
import { useNonLockedList } from "@hooks/useNonLockedList";
import { useTransactionViewConfig } from "@components/transactions/views/useTransactionViewConfig";
import { InfiniteScrollSentinel } from "@design-system/InfiniteScrollSentinel/InfiniteScrollSentinel";
import { Loader } from "@design-system/Loader/Loader";
import { Picker } from "@design-system/Picker/Picker.lib";
import type { PickerItemData } from "@design-system/Picker/Picker.types";
import {
    useRuleTransactionCounts,
    useAppliedTransactionsForRuleQuery,
    useMatchingTransactionsForRuleInfiniteQuery,
} from "@api/rules.api";
import {
    FormRuleActionType,
    mapFormActionTypeToRuleActionType,
    TransactionRuleConditionFormSchema,
    TransactionRuleFormSchema,
} from "../constants";
import { ColumnSettings } from "@design-system/ViewToolbar/ColumnSettings";
import { ViewTable } from "@design-system/ViewTable/ViewTable";
import { TransactionSortValue } from "../../../../transactions/useSort";
import { conditionFormRowToModel } from "../formToModel.helpers";
import { useRuleTransactionSelection } from "../../shared/useRuleTransactionSelection";
import { useRuleTransactionsPreviewView } from "./useRuleTransactionsPreviewView";
import styles from "./RuleTransactionsPreview.module.scss";
import { EmptyState } from "designSystem/EmptyState/EmptyState";

const MIN_TRANSACTIONS_COUNT_TO_WARN = 200;

type PreviewMode = "matching" | "applied";

interface Props {
    rule?: TransactionRule;
    type: RuleGroupType;
}

export const RuleTransactionsPreview: React.FC<Props> = ({ rule, type }) => {
    const [scrollContainer, setScrollContainer] =
        useState<HTMLDivElement | null>(null);
    const [previewMode, setPreviewMode] = useState<PreviewMode>("matching");
    const [conditionsField] =
        useField<TransactionRuleConditionFormSchema[]>("conditions");
    const [{ value: actionsValue }] =
        useField<TransactionRuleFormSchema["actions"]>("actions");
    const [{ value: excludeSplitTransactions }] = useField<
        TransactionRuleFormSchema["excludeSplitTransactions"]
    >("excludeSplitTransactions");

    const splitAction = useMemo(
        () =>
            actionsValue.find(
                (action) => action?.actionType === FormRuleActionType.SPLIT,
            ),
        [actionsValue],
    );
    const isSplitRule = type === RuleGroupType.SPLIT;
    const isAccountingRule = type === RuleGroupType.ACCOUNTING;
    const isEditMode = !!rule;

    const conditions = useMemo(
        () =>
            conditionsField.value
                .map(conditionFormRowToModel)
                .filter((v) => v !== undefined),
        [conditionsField.value],
    );

    const transactionCounts = useRuleTransactionCounts();
    const appliedCount = rule ? (transactionCounts.data?.[rule.id] ?? 0) : 0;

    const mappedSplitAction = useMemo(() => {
        if (!splitAction?.value?.splitAction) {
            return {};
        }

        const mappedSplitConfigs = (
            splitAction.value.splitAction.splits ?? []
        ).map((split) => {
            const amount = splitAction.value?.splitAction?.isPercentage
                ? (split.percentageAmount ?? undefined)
                : (split.amount ?? undefined);

            return {
                categoryId: split.categoryId,
                entityId: split.entityId,
                splitDescription: split.splitDescription,
                amount,
                classIds: split.classIds,
                counterpartyId: split.counterpartyId,
                accountId: split.accountId,
            };
        });

        return {
            ...splitAction.value.splitAction,
            splits: mappedSplitConfigs,
        };
    }, [splitAction]);

    const splitActionParseResult =
        SplitConfigSchema.safeParse(mappedSplitAction);

    // Draft action TYPES drive the preview's lock-safety: a memo-only (lock-safe)
    // rule must include locked rows in the count, matching what apply does.
    // Without this the BE falls back to the conservative non-lock-safe path and
    // undercounts locked rows for lock-safe rules.
    const previewActions = useMemo(() => {
        const ruleActions = actionsValue
            .map((action) => action?.actionType)
            .filter(
                (actionType): actionType is FormRuleActionType => !!actionType,
            )
            .map((actionType) => ({
                actionType: mapFormActionTypeToRuleActionType(actionType),
            }));
        return ruleActions.length > 0 ? ruleActions : undefined;
    }, [actionsValue]);

    const noConditions = conditions.length < 2;
    const matchingTransactions = useMatchingTransactionsForRuleInfiniteQuery({
        groupType: type,
        conditions,
        ruleId: rule?.id,
        actions: previewActions,
        splitActionConfig: splitActionParseResult.success
            ? splitActionParseResult.data
            : undefined,
        excludeSplitTransactions,
        enabled: !noConditions,
    });

    const matchingTotal = matchingTransactions.data?.pages[0]?.total;

    const appliedTransactions = useAppliedTransactionsForRuleQuery({
        ruleId: rule?.id,
        enabled: previewMode === "applied" && isEditMode,
    });

    const isShowingApplied = previewMode === "applied" && isEditMode;

    const activeQuery = isShowingApplied
        ? appliedTransactions
        : matchingTransactions;

    const matchingFlatTransactions = useMemo(
        () =>
            matchingTransactions.data?.pages.flatMap(
                (p) => p?.transactions ?? [],
            ) ?? [],
        [matchingTransactions.data?.pages],
    );

    const appliedFlatTransactions = useMemo(
        () =>
            (appliedTransactions.data?.pages.flat().filter(Boolean) ??
                []) as TransactionListItem[],
        [appliedTransactions.data?.pages],
    );

    const flattenedTransactions = isShowingApplied
        ? appliedFlatTransactions
        : matchingFlatTransactions;

    const nonLockedTransactions = useNonLockedList(flattenedTransactions);
    const nonLockedIds = useMemo(
        () => new Set(nonLockedTransactions.map((t) => t.id)),
        [nonLockedTransactions],
    );

    // Split-rule previews fan out one transaction into several rows; they all
    // share the same numeric `id`, so we key rows by `previewRowId` when it's
    // present (set by the backend in `prepareSplitRulePreview`).
    const getTransactionRowId = useCallback(
        (transaction: TransactionListItem) =>
            transaction.previewRowId ?? String(transaction.id),
        [],
    );

    const activeTotal = isShowingApplied ? appliedCount : matchingTotal;
    const noMatchingTransactions = activeTotal === 0;

    const { view, defaultView, handleViewChange } =
        useRuleTransactionsPreviewView({
            isSplitRule,
            isAccountingRule,
            isShowingApplied,
        });

    const {
        applyToExistingTransactions,
        enableRowSelection,
        handleRowSelectionChange,
        rowSelection,
        selectedTransactionsCount,
        selectAllControl,
    } = useRuleTransactionSelection({
        flattenedTransactions,
        isShowingApplied,
        matchingTotal,
        nonLockedIds,
        getRowKey: getTransactionRowId,
    });

    const tableMeta = useMemo(
        () => ({
            callbacks: {
                calculateSplitSiblings: (transaction: TransactionListItem) => {
                    const rowId = getTransactionRowId(transaction);
                    const index = flattenedTransactions.findIndex(
                        (t) => getTransactionRowId(t) === rowId,
                    );
                    if (index === -1) {
                        return {
                            hasTopSibling: false,
                            hasBottomSibling: false,
                        };
                    }
                    return calculateSplitSiblings(
                        transaction,
                        index,
                        flattenedTransactions,
                    );
                },
            },
        }),
        [flattenedTransactions, getTransactionRowId],
    );

    const emptyView = useCallback(() => {
        if (!isShowingApplied && noConditions) {
            return (
                <EmptyState header="Add conditions to view impacted transactions" />
            );
        }
        if (noMatchingTransactions) {
            return (
                <EmptyState
                    header="No transactions match selected conditions."
                    body="You can still create this rule and apply it going forward."
                />
            );
        }
        return null;
    }, [noConditions, noMatchingTransactions, isShowingApplied]);

    const fetchNextPage = activeQuery.fetchNextPage;
    const hasNextPage = activeQuery.hasNextPage;
    const isLoading = activeQuery.isLoading;
    const isFetchingNextPage = activeQuery.isFetchingNextPage;

    const appendContent = useMemo(
        () => (
            <>
                <InfiniteScrollSentinel
                    scrollRoot={scrollContainer}
                    onVisible={() => fetchNextPage()}
                    disabled={!hasNextPage || isFetchingNextPage}
                />
                {(hasNextPage || isLoading) && <Loader />}
            </>
        ),
        [
            scrollContainer,
            hasNextPage,
            isLoading,
            isFetchingNextPage,
            fetchNextPage,
        ],
    );

    const tableData = useMemo(() => {
        if (!isShowingApplied && noConditions) {
            return [];
        }
        if (activeQuery.isLoading && flattenedTransactions.length === 0) {
            return null;
        }
        if (flattenedTransactions.length > 0) {
            return flattenedTransactions;
        }
        if (noMatchingTransactions) {
            return [];
        }
        return null;
    }, [
        noConditions,
        activeQuery.isLoading,
        flattenedTransactions,
        noMatchingTransactions,
        isShowingApplied,
    ]);

    const transactionsViewConfig = useTransactionViewConfig();

    const matchingCountSuffix =
        matchingTotal != null && matchingTotal > 0 ? ` (${matchingTotal})` : "";
    const matchingLabel = `Matching conditions${matchingCountSuffix}`;
    const appliedLabel = `Was applied to (${appliedCount})`;
    const previewModeItems: Array<PickerItemData<PreviewMode>> = [
        { value: "matching", label: matchingLabel },
        { value: "applied", label: appliedLabel },
    ];

    const warningMessage = useMemo(() => {
        if (previewMode !== "matching" || !applyToExistingTransactions) {
            return null;
        }
        if (selectedTransactionsCount < MIN_TRANSACTIONS_COUNT_TO_WARN) {
            return null;
        }
        return `This change affects ${selectedTransactionsCount} transactions and may take a few minutes to apply.`;
    }, [applyToExistingTransactions, previewMode, selectedTransactionsCount]);

    return (
        <>
            <div className={styles.header}>
                <div className={styles.headerLeading}>
                    {isEditMode ? (
                        <Picker.Select
                            items={previewModeItems}
                            value={previewMode}
                            onValueChange={(value) => {
                                if (value !== null) {
                                    setPreviewMode(value);
                                }
                            }}
                            trigger={
                                <Picker.Button variant="secondary" size="sm" />
                            }
                        />
                    ) : (
                        <span className={styles.title}>{matchingLabel}</span>
                    )}
                    {warningMessage && (
                        <span className={styles.warning}>{warningMessage}</span>
                    )}
                </div>
                <ColumnSettings
                    view={view}
                    viewConfig={transactionsViewConfig}
                    defaultView={defaultView}
                    onViewChange={handleViewChange}
                />
            </div>
            <div style={{ height: "calc(100% - 70px)" }}>
                <ViewTable
                    onScrollContainerMount={setScrollContainer}
                    data={tableData}
                    view={view}
                    config={transactionsViewConfig}
                    rowSelection={rowSelection}
                    onRowSelectionChange={handleRowSelectionChange}
                    selectAllControl={selectAllControl}
                    enableRowSelection={enableRowSelection}
                    emptyView={emptyView}
                    appendContent={appendContent}
                    tableMeta={tableMeta}
                    onViewChange={handleViewChange}
                    sort={TransactionSortValue.DATE_DESC}
                    getRowId={getTransactionRowId}
                    style={{ height: "100%" }}
                />
            </div>
        </>
    );
};
